import * as React from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '../../lib/utils';

// Single, collapsible accordion (the only mode the marketing pages use).
const AccordionContext = React.createContext({ openValue: null, toggle: () => {} });
const ItemContext = React.createContext('');

function Accordion({ className, children }) {
	const [openValue, setOpenValue] = React.useState(null);
	const toggle = React.useCallback(
		(value) => setOpenValue((current) => (current === value ? null : value)),
		[]
	);
	return (
		<AccordionContext.Provider value={{ openValue, toggle }}>
			<div className={className}>{children}</div>
		</AccordionContext.Provider>
	);
}

function AccordionItem({ className, value, children }) {
	const { openValue } = React.useContext(AccordionContext);
	return (
		<ItemContext.Provider value={value}>
			<div
				data-state={openValue === value ? 'open' : 'closed'}
				className={cn('border-b', className)}
			>
				{children}
			</div>
		</ItemContext.Provider>
	);
}

function AccordionTrigger({ className, children }) {
	const { openValue, toggle } = React.useContext(AccordionContext);
	const value = React.useContext(ItemContext);
	const open = openValue === value;
	return (
		<h3 className="flex">
			<button
				type="button"
				aria-expanded={open}
				data-state={open ? 'open' : 'closed'}
				onClick={() => toggle(value)}
				className={cn(
					'flex flex-1 items-center justify-between py-4 text-sm font-medium cursor-pointer transition-all hover:underline text-left [&[data-state=open]>svg]:rotate-180',
					className
				)}
			>
				{children}
				<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
			</button>
		</h3>
	);
}

function AccordionContent({ className, children }) {
	const { openValue } = React.useContext(AccordionContext);
	const value = React.useContext(ItemContext);
	if (openValue !== value) return null;
	return (
		<div className="overflow-hidden text-sm">
			<div className={cn('pb-4 pt-0', className)}>{children}</div>
		</div>
	);
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
